import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Seo } from "@/components/Layout";
import { LogoMark } from "@/components/Logo";
import AudioButton from "@/components/AudioButton";
import api from "@/lib/api";
import { Overline } from "@/components/Bits";
import {
  MessageSquare, Route as RouteIcon, Wrench, BookOpen, Check, X, ArrowRight,
  Target, Repeat, GraduationCap, MapPin, Sparkles
} from "lucide-react";

const DEMO = {
  prompt: "¿Cómo dirías 'Me llamo Ana' en alemán?",
  options: ["Ich heisse Ana", "Ich komme Ana", "Ich wohne Ana", "Ich bin Ana Jahre"],
  answer: "Ich heisse Ana",
  explanation: "Para presentarte usamos 'Ich heisse ...'. El verbo 'heisse' (me llamo) va en segunda posición.",
};

function Demo() {
  const [sel, setSel] = useState(null);
  const [checked, setChecked] = useState(false);
  return (
    <div className="rounded-lg border border-border bg-card p-6 shadow-sm" data-testid="hero-demo">
      <div className="flex items-center gap-2 mb-4">
        <Sparkles size={16} className="text-primary" />
        <span className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Prueba en 10 segundos</span>
      </div>
      <p className="font-display font-semibold text-lg mb-4">{DEMO.prompt}</p>
      <div className="grid gap-2">
        {DEMO.options.map((o) => {
          const right = checked && o === DEMO.answer;
          const wrong = checked && sel === o && o !== DEMO.answer;
          return (
            <button key={o} disabled={checked} onClick={() => { setSel(o); setChecked(true); }}
              data-testid={`demo-option-${o}`}
              className={`text-left px-4 py-2.5 rounded-md border text-sm font-medium transition-colors flex items-center justify-between
                ${right ? "border-emerald-500 bg-emerald-500/10" : wrong ? "border-destructive bg-destructive/10" : "border-border hover:border-primary/50 hover:bg-secondary"}`}>
              {o}
              {right && <Check size={16} className="text-emerald-600" />}
              {wrong && <X size={16} className="text-destructive" />}
            </button>
          );
        })}
      </div>
      {checked && (
        <div className="mt-4 rounded-md bg-secondary p-4 text-sm" data-testid="demo-feedback">
          <p className="font-semibold mb-1">{sel === DEMO.answer ? "¡Muy bien!" : "Casi..."}</p>
          <p className="text-muted-foreground">{DEMO.explanation}</p>
          <Link to="/prueba-de-nivel"><Button size="sm" className="mt-3 rounded-full">Haz la prueba completa <ArrowRight size={14} className="ml-1" /></Button></Link>
        </div>
      )}
    </div>
  );
}

const HOW = [
  { icon: Target, t: "Haz la prueba de nivel", d: "En pocos minutos sabrás por dónde empezar: principiante, A1, A2 o B1." },
  { icon: RouteIcon, t: "Sigue tu ruta", d: "Lecciones breves con situaciones reales de la vida y el trabajo en Suiza." },
  { icon: Repeat, t: "Practica y repasa", d: "Ejercicios con corrección inmediata y repaso de tus errores frecuentes." },
];

const REAL = [
  { t: "Buscar piso", d: "Entiende anuncios y habla con propietarios." },
  { t: "Primer día de trabajo", d: "Preséntate y comprende instrucciones básicas." },
  { t: "Transporte público", d: "Compra billetes y pregunta por direcciones." },
  { t: "Compras y farmacia", d: "Vocabulario cotidiano para el día a día." },
];

export default function Home() {
  const [faq, setFaq] = useState([]);
  const [word, setWord] = useState(null);
  useEffect(() => {
    api.get("/faq").then(({ data }) => setFaq(data)).catch(() => {});
    api.get("/word-of-day").then(({ data }) => setWord(data)).catch(() => {});
  }, []);

  return (
    <>
      <Seo title="Aprende el alemán que realmente necesitas en Suiza"
        description="Lecciones breves, situaciones reales y explicaciones claras en español para vivir, trabajar y comunicarte con confianza en la Suiza germanófona." />

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="absolute inset-0 hs-grid-bg opacity-40" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs font-medium mb-6">
                <LogoMark size={16} /> El alemán para vivir y trabajar en Suiza
              </div>
              <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
                Aprende el alemán que <span className="text-primary">realmente necesitas</span> en Suiza.
              </h1>
              <p className="mt-6 text-base md:text-lg text-muted-foreground max-w-xl leading-relaxed">
                Lecciones breves, situaciones reales y explicaciones claras en español para ayudarte a vivir, trabajar y comunicarte con confianza.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/prueba-de-nivel"><Button size="lg" className="rounded-full" data-testid="hero-level-test">Haz la prueba de nivel</Button></Link>
                <Link to="/cursos"><Button size="lg" variant="outline" className="rounded-full" data-testid="hero-explore">Explorar lecciones</Button></Link>
              </div>
            </div>
            <div className="lg:col-span-5"><Demo /></div>
          </div>
        </div>
      </section>

      {/* 1. SITUACIONES REALES */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
        <Overline>Aprende con situaciones reales</Overline>
        <h2 className="font-display font-bold text-3xl md:text-4xl tracking-tight max-w-2xl">No memorices listas. Resuelve tu día a día en Suiza.</h2>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {REAL.map((r) => (
            <div key={r.t} className="rounded-lg border border-border bg-card p-6 hover:-translate-y-1 hover:border-primary/40 transition-transform" data-testid={`real-${r.t}`}>
              <MapPin size={20} className="text-accent mb-4" />
              <h3 className="font-display font-semibold text-lg">{r.t}</h3>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{r.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 2. CÓMO FUNCIONA */}
      <section className="border-y border-border bg-card">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
          <Overline>Cómo funciona Hallo Suiza</Overline>
          <h2 className="font-display font-bold text-3xl md:text-4xl tracking-tight">Tres pasos, sin complicaciones.</h2>
          <div className="mt-10 grid md:grid-cols-3 gap-6">
            {HOW.map((h, i) => (
              <div key={h.t} className="relative rounded-lg border border-border bg-background p-7">
                <span className="absolute top-6 right-6 font-display font-black text-4xl text-primary/15">0{i + 1}</span>
                <h.icon size={22} className="text-primary mb-4" />
                <h3 className="font-display font-semibold text-lg">{h.t}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{h.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 3. RUTAS + 4. HERRAMIENTAS */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 lg:py-20 grid lg:grid-cols-2 gap-8">
        <div className="rounded-lg border border-border bg-card p-8">
          <RouteIcon size={22} className="text-accent mb-4" />
          <Overline>Rutas de aprendizaje</Overline>
          <h3 className="font-display font-bold text-2xl">Alemán esencial para empezar en Suiza</h3>
          <p className="mt-3 text-sm text-muted-foreground leading-relaxed">Nivel A1. Diez módulos con lo indispensable para tus primeras semanas: presentarte, moverte, comprar, buscar piso y dar tus primeros pasos en el trabajo.</p>
          <Link to="/cursos"><Button variant="outline" className="mt-6 rounded-full">Ver la ruta <ArrowRight size={14} className="ml-1" /></Button></Link>
        </div>
        <div className="rounded-lg border border-border bg-card p-8">
          <Wrench size={22} className="text-primary mb-4" />
          <Overline>Herramientas gratuitas</Overline>
          <h3 className="font-display font-bold text-2xl">Practica sin registrarte</h3>
          <ul className="mt-4 grid grid-cols-2 gap-2 text-sm text-muted-foreground">
            {["Entrenador der/die/das", "Vocabulario por profesión", "Orden de palabras", "Comparador de expresiones", "Frases cotidianas", "Plan de estudio"].map((x) => (
              <li key={x} className="flex items-center gap-2"><Check size={14} className="text-primary" />{x}</li>
            ))}
          </ul>
          <Link to="/herramientas"><Button variant="outline" className="mt-6 rounded-full">Ver herramientas <ArrowRight size={14} className="ml-1" /></Button></Link>
        </div>
      </section>

      {/* 5. ESTÁNDAR VS SUIZO */}
      <section className="border-y border-border bg-card">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
          <Overline>Estándar vs. suizo</Overline>
          <h2 className="font-display font-bold text-3xl md:text-4xl tracking-tight max-w-2xl">Aprende el estándar y entiende el suizo.</h2>
          <div className="mt-10 overflow-hidden rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead className="bg-secondary">
                <tr><th className="text-left p-4 font-semibold">Alemán estándar</th><th className="text-left p-4 font-semibold">Forma habitual en Suiza</th><th className="text-left p-4 font-semibold">Contexto</th></tr>
              </thead>
              <tbody>
                {[["Guten Tag", "Grüezi", "Saludo formal"], ["Fahrrad", "Velo", "Bicicleta"], ["danke", "merci", "Agradecer"], ["Entschuldigung", "Exgüsi", "Disculpa"]].map((r) => (
                  <tr key={r[0]} className="border-t border-border">
                    <td className="p-4 font-medium">{r[0]}</td><td className="p-4 text-accent font-medium">{r[1]}</td><td className="p-4 text-muted-foreground">{r[2]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Link to="/herramientas/comparador"><Button variant="ghost" className="mt-4">Ver el comparador completo <ArrowRight size={14} className="ml-1" /></Button></Link>
        </div>
      </section>

      {/* 6. GUÍAS + palabra del día */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 lg:py-20 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <Overline>Últimas guías revisadas</Overline>
          <h2 className="font-display font-bold text-3xl tracking-tight">Vivir y trabajar en Suiza</h2>
          <p className="mt-3 text-sm text-muted-foreground max-w-xl leading-relaxed">
            Guías prácticas para hispanohablantes que viven o quieren vivir en Suiza. Publicamos y actualizamos periódicamente nuevos recursos sobre alemán y vida en Suiza.
          </p>
          <Link to="/guias"><Button variant="outline" className="mt-6 rounded-full">Ver todas las guías <ArrowRight size={14} className="ml-1" /></Button></Link>
        </div>
        {word && (
          <div className="rounded-lg border border-border bg-card p-7">
            <Overline>Palabra del día</Overline>
            <p className="font-display font-black text-2xl text-primary flex items-center gap-2">{word.de}<AudioButton text={word.de} size={18} /></p>
            <p className="text-sm text-muted-foreground mt-1">{word.es}</p>
            <div className="mt-4 rounded-md bg-secondary p-3 text-sm">
              <p className="font-medium">{word.example_de}</p>
              <p className="text-muted-foreground">{word.example_es}</p>
            </div>
          </div>
        )}
      </section>

      {/* 7. PLANES — columna Premium oculta temporalmente durante la revisión de AdSense (código intacto; reactivar cambiando false por true en el bloque Premium). */}
      <section className="border-y border-border bg-card">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
          <Overline>Planes</Overline>
          <h2 className="font-display font-bold text-3xl md:text-4xl tracking-tight">Empieza gratis. Sube de nivel cuando quieras.</h2>
          <div className="mt-10 grid gap-6 max-w-md mx-auto">
            <div className="rounded-lg border border-border bg-background p-8">
              <h3 className="font-display font-bold text-xl">Gratuito</h3>
              <p className="text-sm text-muted-foreground mt-1">Para empezar hoy mismo.</p>
              <ul className="mt-5 space-y-2 text-sm">
                {["Lecciones iniciales", "3 actividades personalizadas al día", "Progreso básico", "Todas las herramientas gratuitas"].map((x) => (
                  <li key={x} className="flex gap-2"><Check size={16} className="text-primary shrink-0" />{x}</li>
                ))}
              </ul>
              <Link to="/registro"><Button className="mt-6 w-full rounded-full">Empieza gratis</Button></Link>
            </div>
            {false && (
            <div className="rounded-lg border-2 border-primary bg-background p-8 relative">
              <span className="absolute -top-3 left-8 rounded-full bg-primary text-primary-foreground text-xs px-3 py-1 font-medium">Próximamente</span>
              <h3 className="font-display font-bold text-xl">Premium</h3>
              <p className="text-sm text-muted-foreground mt-1">Funciones avanzadas para acelerar tu aprendizaje.</p>
              <ul className="mt-5 space-y-2 text-sm">
                {["Sin anuncios", "Todos los cursos", "Conversaciones con IA", "Repasos inteligentes y estadísticas", "Certificado interno (no oficial)"].map((x) => (
                  <li key={x} className="flex gap-2"><Check size={16} className="text-primary shrink-0" />{x}</li>
                ))}
              </ul>
              <Link to="/precios"><Button variant="outline" className="mt-6 w-full rounded-full">Ver planes</Button></Link>
            </div>
            )}
          </div>
        </div>
      </section>

      {/* 8. FAQ */}
      <section className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
        <Overline>Preguntas frecuentes</Overline>
        <h2 className="font-display font-bold text-3xl tracking-tight mb-8">Lo que conviene saber</h2>
        <Accordion type="single" collapsible className="w-full" data-testid="home-faq">
          {faq.map((f, i) => (
            <AccordionItem key={i} value={`f${i}`}>
              <AccordionTrigger className="text-left font-display">{f.q}</AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* 9. CTA FINAL */}
      <section className="border-t border-border">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="rounded-lg bg-primary text-primary-foreground p-10 lg:p-16 relative overflow-hidden">
            <GraduationCap size={120} className="absolute -right-6 -bottom-6 opacity-10" />
            <h2 className="font-display font-black text-3xl md:text-4xl tracking-tight max-w-2xl">¿Por dónde empezar? Descúbrelo en unos minutos.</h2>
            <p className="mt-4 text-primary-foreground/90 max-w-xl">Haz la prueba de nivel y recibe una ruta recomendada. Es orientativa y gratuita.</p>
            <Link to="/prueba-de-nivel"><Button size="lg" variant="secondary" className="mt-8 rounded-full" data-testid="cta-final-test">Haz la prueba de nivel <ArrowRight size={16} className="ml-1" /></Button></Link>
          </div>
        </div>
      </section>
    </>
  );
}
